import Select from "react-select";
import React from "react";

/**
 * The main purpose of this component is to give ids to the objects we are using in select so that useClickOutside will work.
 * The issue with the normal select is that for overflow to work right certain aspects of it are rendered directly in the
 * document body (additionally, we sometimes manually tell it to render there to bypass overflow restrictions. See menuPortalTarget prop in select).
 * We can also use this component to apply global styles to select components and customize individual components in the select.
 *
 * This component was built based off the custom components example(s) in the `react-select documentation`
 *
 * @param select
 * @param options
 * @param onChange
 * @returns {Element}
 * @constructor
 * @see https://react-select.com/styles#cx-and-custom-components react-select documentation.
 */
export default function LibrarySelect({select, options, onChange}) {
    const CustomOption = (props) => {
        const {
            children,
            className,
            cx,
            getStyles,
            isDisabled,
            isFocused,
            isSelected,
            innerRef,
            innerProps,
        } = props;

        return (
            <div id="option"
                ref={innerRef}
                style={getStyles('option', props)}
                className={cx(
                    {
                        option: true,
                        'option--is-disabled': isDisabled,
                        'option--is-focused': isFocused,
                        'option--is-selected': isSelected,
                    },
                    className
                )}
                {...innerProps}
            >
                {children}
            </div>
        );
    };

    const CustomMultiValueRemove = (props) => {
        const {
            innerRef,
            innerProps,
            isDisabled,
            cx,
            getStyles
        } = props;

        return (
            <span
                id="multi-val-remove" // Add an id for easier detection
                ref={innerRef}
                style={getStyles ? getStyles('multiValueRemove', props) : {paddingBottom: 2}} // bump up the x cause it is not centered in the font
                className={cx ? cx(
                    {
                        'multi-val-remove': true,
                        'multi-val-remove--is-disabled': isDisabled,
                    }
                ) : {}}
                {...innerProps}
            >
            ×
        </span>
        );
    };



    const CustomMenu = (props) => {
        const {
            children,
            className,
            cx,
            getStyles,
            innerRef,
            innerProps,
        } = props;

        return (
            <div
                id="custom-menu"
                ref={innerRef}
                style={getStyles('menu', props)} // Inline styles from React-Select
                className={cx(
                    {
                        menu: true, // React-Select default styles
                    },
                    className
                )}
                {...innerProps} // Spread the rest of the props
            >
                {children}
            </div>
        );
    };


    return <Select
        isMulti
        className="allow-overflow"
        defaultValue={{
            value: null, label: null, isFixed: true, isDisabled: true
        }}
        value={select}
        options={options}
        menuPortalTarget={document.body}
        styles={{
            menu: (styles) => {
                return {
                    ...styles,
                    top: -75,
                    left: -8,
                    textAlign: "left",
                    color: "gray",
                    fontWeight: "bold",
                    margin: 10,
                    fontSize: 16
                };
            },
        }}
        getOptionValue={(option) => option.value} // Unique key for React
        getOptionLabel={(option) => `${option.label}`} // Customize label if needed
        components={{
            Option: CustomOption,
            Menu: CustomMenu,
            MultiValueRemove: CustomMultiValueRemove,
        }}
        closeMenuOnSelect={false}
        onChange={(arr) => {
            onChange(arr)
        }}
    />
}